import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { ToastService } from '../../core/services/toast.service';
import { ModalComponent } from '../../shared/components/modal/modal.component';

export type SecurityServerRole =
  | 'SECURITY_OFFICER'
  | 'REGISTRATION_OFFICER'
  | 'SERVER_ADMINISTRATOR'
  | 'SYSTEM_ADMINISTRATOR'
  | 'SERVER_OBSERVER';

export interface RoleDefinition {
  code: SecurityServerRole;
  name: string;
  badgeClass: string;
  description: string;
}

export const SECURITY_ROLES: RoleDefinition[] = [
  {
    code: 'SECURITY_OFFICER',
    name: 'Security Officer',
    badgeClass: 'bg-indigo-50 text-indigo-800 border-indigo-200',
    description: 'Manages security-related settings such as authentication keys, certificates, and security tokens. Responsible for security configuration.'
  },
  {
    code: 'REGISTRATION_OFFICER',
    name: 'Registration Officer',
    badgeClass: 'bg-blue-50 text-blue-800 border-blue-200',
    description: "Manages the Security Server's registration with the Central Server, including members, clients, and service registration-related activities."
  },
  {
    code: 'SERVER_ADMINISTRATOR',
    name: 'Server Administrator',
    badgeClass: 'bg-emerald-50 text-emerald-800 border-emerald-300',
    description: 'Manages the Security Server configuration, such as services, clients, connections, and operational settings.'
  },
  {
    code: 'SYSTEM_ADMINISTRATOR',
    name: 'System Administrator',
    badgeClass: 'bg-purple-50 text-purple-800 border-purple-200',
    description: 'Manages the underlying server/system, including system-level configuration and maintenance. Usually has the broadest administrative access.'
  },
  {
    code: 'SERVER_OBSERVER',
    name: 'Server Observer',
    badgeClass: 'bg-slate-100 text-slate-700 border-slate-300',
    description: 'Read-only access. Can view Security Server configuration, status, and information but cannot make changes.'
  }
];

export interface SecurityServerApiKeyRecord {
  id: string;
  serverName: string;
  serverCode: string;
  hostEndpoint: string;
  role: SecurityServerRole;
  roleName: string;
  roleDescription: string;
  label: string;
  tokenValue: string;
  status: 'ACTIVE' | 'REVOKED';
  createdAt: string;
  lastUsed: string;
  showSecret?: boolean;
}

@Component({
  selector: 'app-engine-config',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent],
  template: `
    <div class="space-y-6 font-sans">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div class="flex items-center gap-2 mb-1">
            <span class="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300">
              Interoperability Engine & Config
            </span>
            <span class="text-xs text-slate-400 font-mono">X-Road v7.3.2-ZANX</span>
          </div>
          <h1 class="text-2xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2.5">
            <svg class="w-7 h-7 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Engine & Config
          </h1>
          <p class="text-xs text-slate-500 mt-0.5">
            Security Server node operational status, configuration anchor, installation guides, and role-based API keys for
            <strong class="text-slate-800">{{ authService.institutionName() }}</strong>.
          </p>
        </div>

        <!-- Header Actions -->
        <div class="flex flex-wrap items-center gap-2">
          @if (activeTab() === 'overview') {
            <button
              type="button"
              (click)="runPingTest()"
              [disabled]="isTesting()"
              class="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs border border-slate-300 shadow-2xs transition cursor-pointer disabled:opacity-50"
            >
              <svg class="w-4 h-4 text-emerald-600" [class.animate-spin]="isTesting()" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>{{ isTesting() ? 'Pinging Node...' : 'Test Connection Ping' }}</span>
            </button>

            <button
              type="button"
              (click)="syncCentralServices()"
              [disabled]="isSyncing()"
              class="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs border border-slate-300 shadow-2xs transition cursor-pointer disabled:opacity-50"
            >
              <svg class="w-4 h-4 text-emerald-600" [class.animate-spin]="isSyncing()" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>{{ isSyncing() ? 'Syncing...' : 'Sync Central' }}</span>
            </button>

            <button
              type="button"
              (click)="openAddModal()"
              class="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-md shadow-emerald-600/20 transition cursor-pointer"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              <span>Add Security Server API Key</span>
            </button>
          } @else {
            <button
              type="button"
              (click)="downloadAnchor()"
              class="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs border border-slate-300 shadow-2xs transition cursor-pointer"
            >
              <svg class="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Download Anchor XML</span>
            </button>

            <button
              type="button"
              (click)="setTab('overview')"
              class="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-md shadow-emerald-600/20 transition cursor-pointer"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              <span>Manage API Keys</span>
            </button>
          }
        </div>
      </div>

      <!-- Main Navigation Tabs: 1. Prerequisites & Installation, 2. Security Server & API Keys -->
      <div class="border-b border-slate-200">
        <nav class="flex space-x-6" aria-label="Tabs">
          <button
            type="button"
            (click)="setTab('installation')"
            [ngClass]="activeTab() === 'installation'
              ? 'border-emerald-600 text-emerald-700 font-extrabold'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 font-semibold'"
            class="py-3 px-1 border-b-2 text-xs flex items-center gap-2 cursor-pointer transition-colors"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>Prerequisites & Installation</span>
            <span class="px-2 py-0.5 rounded-full text-[10px] font-mono bg-slate-100 text-slate-700">
              Guide & Setup
            </span>
          </button>

          <button
            type="button"
            (click)="setTab('overview')"
            [ngClass]="activeTab() === 'overview'
              ? 'border-emerald-600 text-emerald-700 font-extrabold'
              : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300 font-semibold'"
            class="py-3 px-1 border-b-2 text-xs flex items-center gap-2 cursor-pointer transition-colors"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
            <span>Security Server & API Keys</span>
            <span class="px-2 py-0.5 rounded-full text-[10px] font-mono bg-emerald-100 text-emerald-800">
              {{ activeTokensCount() }} Keys
            </span>
          </button>
        </nav>
      </div>

      <!-- TAB 1: Security Server & API Keys (Current Content) -->
      @if (activeTab() === 'overview') {
        <div class="space-y-6 animate-in fade-in duration-200">
          <!-- Security Server Summary Banner -->
          <div class="p-5 rounded-2xl bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-950 text-white shadow-xl border border-slate-700/60 relative overflow-hidden">
            <div class="absolute -right-10 -bottom-10 w-48 h-48 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none"></div>

            <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
              <div class="flex items-start gap-4">
                <div class="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-400 shrink-0 shadow-inner">
                  <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <div class="flex items-center gap-2.5 flex-wrap">
                    <h3 class="text-base font-extrabold tracking-wide text-white">Security Server Proxy Node</h3>
                    <span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-black bg-emerald-400/20 text-emerald-300 border border-emerald-400/40">
                      <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span>
                      NODE ONLINE & HEALTHY
                    </span>
                    <span class="font-mono text-[11px] text-slate-300 px-2 py-0.5 rounded bg-white/10">
                      {{ serverHost() }}
                    </span>
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mt-3 text-xs">
                    <div>
                      <span class="text-[10px] font-mono uppercase tracking-wider text-slate-400 block">Security Server Code</span>
                      <span class="font-mono font-bold text-emerald-300">{{ serverCode() }}</span>
                    </div>
                    <div>
                      <span class="text-[10px] font-mono uppercase tracking-wider text-slate-400 block">Institution Member Code</span>
                      <span class="font-mono font-bold text-slate-200">{{ memberCode() }}</span>
                    </div>
                    <div>
                      <span class="text-[10px] font-mono uppercase tracking-wider text-slate-400 block">Last Central Sync</span>
                      <span class="font-semibold text-slate-300">{{ lastSyncTime() }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Live Node Latency & Configured Keys Stats -->
              <div class="flex items-center gap-3 shrink-0 bg-white/5 border border-white/10 rounded-2xl p-3.5 backdrop-blur-sm">
                <div class="text-center px-3 border-r border-white/10">
                  <div class="text-lg font-black text-emerald-400">{{ latency() }}</div>
                  <div class="text-[10px] text-slate-400 uppercase font-mono tracking-wider">Ping Latency</div>
                </div>
                <div class="text-center px-3 border-r border-white/10">
                  <div class="text-lg font-black text-white">99.98%</div>
                  <div class="text-[10px] text-slate-400 uppercase font-mono tracking-wider">Node Uptime</div>
                </div>
                <div class="text-center px-3">
                  <div class="text-lg font-black text-emerald-300">{{ activeTokensCount() }}</div>
                  <div class="text-[10px] text-slate-400 uppercase font-mono tracking-wider">Active Keys</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Configuration Anchor Box -->
          <div class="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-2.5">
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div class="flex items-center gap-3">
                <div class="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0 border border-emerald-200">
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h4 class="text-xs font-extrabold text-slate-900 uppercase tracking-wide">Zan-X Global Configuration Anchor</h4>
                  <p class="text-[11px] text-slate-500">Anchors your security server trust federation to Zanzibar e-Government Central Server.</p>
                </div>
              </div>

              <button
                type="button"
                (click)="downloadAnchor()"
                class="px-3.5 py-1.5 rounded-xl text-xs font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 transition cursor-pointer inline-flex items-center gap-1.5 shadow-2xs shrink-0"
              >
                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Download Anchor XML</span>
              </button>
            </div>

            <div class="p-2.5 rounded-xl bg-slate-950 font-mono text-[11px] text-emerald-400 overflow-x-auto shadow-inner leading-relaxed">
              <code>INSTANCE: ZANX-CORE | HASH: 8f4c2e...b3a910 | GENERATED: 2026-09-20 08:30:00 UTC</code>
            </div>
          </div>

          <!-- Filter and Search Controls -->
          <div class="glass-panel p-4 rounded-2xl border border-slate-200 bg-white flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm">
            <div class="flex items-center gap-2">
              <span class="text-xs font-bold text-slate-800 uppercase tracking-wider">Role-Based Security Server API Keys</span>
              <span class="px-2 py-0.5 rounded-full text-xs font-mono font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                {{ filteredRecords().length }} of {{ keyRecords().length }} Registered
              </span>
            </div>

            <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <!-- Filter by Role / Key Type Dropdown -->
              <div class="w-full sm:w-56">
                <select
                  [(ngModel)]="selectedRoleFilter"
                  class="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-xs text-slate-700 font-medium focus:border-emerald-500 focus:outline-none"
                >
                  <option value="ALL">All Roles (5 Types)</option>
                  @for (r of roles; track r.code) {
                    <option [value]="r.code">{{ r.name }}</option>
                  }
                </select>
              </div>

              <!-- Filter by Status Dropdown -->
              <div class="w-full sm:w-36">
                <select
                  [(ngModel)]="selectedStatusFilter"
                  class="w-full px-3 py-2 rounded-xl bg-white border border-slate-300 text-xs text-slate-700 font-medium focus:border-emerald-500 focus:outline-none"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="ACTIVE">Active Only</option>
                  <option value="REVOKED">Revoked</option>
                </select>
              </div>

              <!-- Search Input -->
              <div class="w-full sm:w-64">
                <input
                  type="text"
                  [(ngModel)]="searchQuery"
                  placeholder="Search by label, token, role, node..."
                  class="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-300 text-xs text-slate-900 placeholder-slate-400 focus:border-emerald-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          <!-- Single Unified Security Server Details & Role API Keys Table -->
          <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
            <div class="overflow-x-auto">
              <table class="w-full text-left text-xs">
                <thead class="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[11px]">
                  <tr>
                    <th class="p-4">Security Server Details</th>
                    <th class="p-4">API Key Role / Type</th>
                    <th class="p-4">Key Label / Identifier</th>
                    <th class="p-4">Security Server API Key Token</th>
                    <th class="p-4">Status</th>
                    <th class="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 font-medium">
                  @for (rec of filteredRecords(); track rec.id) {
                    <tr class="hover:bg-slate-50/70 transition-colors">
                      <!-- 1. Security Server Details -->
                      <td class="p-4">
                        <div class="space-y-0.5">
                          <div class="font-bold text-slate-900 flex items-center gap-1.5">
                            <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
                            <span>{{ rec.serverName }}</span>
                          </div>
                          <div class="font-mono text-[10px] text-emerald-800 font-bold">{{ rec.serverCode }}</div>
                          <div class="text-[10px] text-slate-400 font-mono truncate max-w-[200px]" [title]="rec.hostEndpoint">
                            {{ rec.hostEndpoint }}
                          </div>
                        </div>
                      </td>

                      <!-- 2. Role / API Key Type -->
                      <td class="p-4">
                        <div class="space-y-1">
                          <span
                            class="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wide border inline-flex items-center gap-1"
                            [ngClass]="getRoleBadgeClass(rec.role)"
                          >
                            <span>{{ rec.roleName }}</span>
                          </span>
                          <p class="text-[11px] text-slate-500 max-w-xs leading-snug">
                            {{ rec.roleDescription }}
                          </p>
                        </div>
                      </td>

                      <!-- 3. Key Label / Identifier -->
                      <td class="p-4">
                        <div class="font-bold text-slate-900">{{ rec.label }}</div>
                        <div class="text-[10px] text-slate-400 mt-0.5">
                          Last used: {{ rec.lastUsed }}
                        </div>
                      </td>

                      <!-- 4. API Key Token (Inserted by user/admin, masked with Reveal & Copy) -->
                      <td class="p-4 whitespace-nowrap">
                        <div class="flex items-center gap-2 bg-slate-50 border border-slate-200 px-2.5 py-1.5 rounded-xl font-mono text-[11px] max-w-xs shadow-2xs">
                          <span class="text-slate-800 font-semibold select-all">
                            {{ rec.showSecret ? rec.tokenValue : getMaskedToken(rec.tokenValue) }}
                          </span>
                          <div class="flex items-center gap-1 ml-auto shrink-0">
                            <button
                              type="button"
                              (click)="rec.showSecret = !rec.showSecret"
                              [title]="rec.showSecret ? 'Hide Token' : 'Reveal Token'"
                              class="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition cursor-pointer"
                            >
                              @if (rec.showSecret) {
                                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                                </svg>
                              } @else {
                                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              }
                            </button>
                            <button
                              type="button"
                              (click)="copyToken(rec.tokenValue)"
                              title="Copy Full Token"
                              class="p-1 rounded text-emerald-700 hover:text-emerald-950 hover:bg-emerald-100 transition cursor-pointer"
                            >
                              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </td>

                      <!-- 5. Status -->
                      <td class="p-4 whitespace-nowrap">
                        @if (rec.status === 'ACTIVE') {
                          <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-300 uppercase tracking-wide inline-flex items-center gap-1">
                            <span class="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                            Active
                          </span>
                        } @else {
                          <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-800 border border-rose-300 uppercase tracking-wide">
                            Revoked
                          </span>
                        }
                      </td>

                      <!-- 6. Actions (Edit / Update, Revoke) -->
                      <td class="p-4 text-right whitespace-nowrap">
                        <div class="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            (click)="openEditModal(rec)"
                            title="Edit / Update Security Server Key"
                            class="p-1.5 rounded-lg text-slate-600 hover:text-emerald-800 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-300 transition cursor-pointer"
                          >
                            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>

                          <button
                            type="button"
                            (click)="revokeToken(rec)"
                            title="Revoke / Deactivate Key"
                            class="p-1.5 rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-50 border border-slate-200 hover:border-rose-300 transition cursor-pointer"
                          >
                            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  }

                  @if (filteredRecords().length === 0) {
                    <tr>
                      <td colspan="6" class="p-8 text-center text-slate-400 text-xs">
                        No security server API keys found matching your criteria.
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      }

      <!-- TAB 2: Prerequisites & Installation Guide (Authentic Zanzibar eGAZ Guide) -->
      @if (activeTab() === 'installation') {
        <div class="space-y-6 animate-in fade-in duration-200">
          <!-- Zanzibar e-Government Authority (eGAZ) Document Header Banner -->
          <div class="p-6 rounded-2xl bg-gradient-to-r from-slate-900 via-emerald-950 to-slate-900 text-white shadow-xl border border-emerald-500/30 relative overflow-hidden">
            <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
              <div class="space-y-1.5">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-400/20 text-emerald-300 border border-emerald-400/40">
                    Revolutionary Government of Zanzibar &bull; eGAZ
                  </span>
                  <span class="text-xs text-slate-300 font-mono">Zan X-Change Installation Standard</span>
                </div>
                <h2 class="text-xl font-extrabold tracking-tight text-white flex items-center gap-2">
                  <svg class="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Zan X-Change Security Server Installation & Configuration Guide
                </h2>
                <p class="text-xs text-slate-300 max-w-3xl leading-relaxed">
                  Official deployment specification issued by President's Office State House, Zanzibar e-Government Authority (eGAZ) for institutions configuring proxy nodes on the Zan X-Change interoperability backbone.
                </p>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  (click)="downloadAnchor()"
                  class="px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 shadow-md shadow-emerald-600/30 transition cursor-pointer inline-flex items-center gap-2"
                >
                  <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <span>Download Central Anchor XML</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Section 1: System Requirements (Section 2 of eGAZ Guide) -->
          <div class="space-y-3">
            <div class="flex items-center gap-2">
              <span class="w-6 h-6 rounded-full bg-emerald-600 text-white font-black text-xs flex items-center justify-center">1</span>
              <h3 class="text-sm font-extrabold text-slate-900 tracking-tight">System & Hardware Requirements (Section 2)</h3>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
              <!-- Operating System (Updated exclusively to Ubuntu 24 or latest) -->
              <div class="p-4 rounded-2xl bg-white border border-emerald-300 shadow-2xs space-y-2 ring-2 ring-emerald-500/10">
                <div class="flex items-center justify-between">
                  <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Required OS</span>
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 uppercase">
                    Mandatory
                  </span>
                </div>
                <div class="text-xs font-black text-emerald-950">Ubuntu Server 24.04 LTS (x86-64) or latest</div>
                <p class="text-[11px] text-slate-600 leading-snug">
                  Only Ubuntu 24.04 LTS or latest x86-64 platform is approved. Previous Ubuntu releases (20.04/22.04) are deprecated.
                </p>
              </div>

              <!-- Hardware Compute (CPU & AES) -->
              <div class="p-4 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-2">
                <div class="flex items-center justify-between">
                  <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">CPU Specifications</span>
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700">AES-NI</span>
                </div>
                <div class="text-xs font-bold text-slate-900">64-bit Dual-Core (Xeon / EPYC)</div>
                <p class="text-[11px] text-slate-500 leading-snug">
                  Intel or AMD compatible with hardware AES instruction set support highly recommended for cryptographic throughput.
                </p>
              </div>

              <!-- Memory -->
              <div class="p-4 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-2">
                <div class="flex items-center justify-between">
                  <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">RAM Memory</span>
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700">Minimum</span>
                </div>
                <div class="text-xs font-bold text-slate-900">At least 8 GB of RAM</div>
                <p class="text-[11px] text-slate-500 leading-snug">
                  8 GB minimum required to run the local message log archiver, PostgreSQL instance, proxy, and signer processes.
                </p>
              </div>

              <!-- Storage & Network Interfaces -->
              <div class="p-4 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-2">
                <div class="flex items-center justify-between">
                  <span class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Storage & Network</span>
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700">RAID / NICs</span>
                </div>
                <div class="text-xs font-bold text-slate-900">200+ GB SSD &bull; Multi-NIC</div>
                <p class="text-[11px] text-slate-500 leading-snug">
                  SSDs with &ge;200 GB (RAID recommended). Multiple NICs &ge;100 Mbps separating internal and external network traffic.
                </p>
              </div>
            </div>
          </div>

          <!-- Section 2: Network Requirements & Port Configuration (Table 1 & Table 2 of eGAZ Guide) -->
          <div class="space-y-4">
            <div class="flex items-center gap-2">
              <span class="w-6 h-6 rounded-full bg-emerald-600 text-white font-black text-xs flex items-center justify-center">2</span>
              <h3 class="text-sm font-extrabold text-slate-900 tracking-tight">Network & Firewall Rules (Section 2.3 & Tables 1-2)</h3>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <!-- Table 1: External & Internal Network Ports -->
              <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs space-y-0">
                <div class="p-3.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <span class="text-xs font-bold text-slate-900 uppercase tracking-wider">Table 1: Ports Allowed for External / Internal Segments</span>
                  <span class="text-[10px] font-mono text-slate-400">eGAZ Reference Data</span>
                </div>
                <div class="overflow-x-auto">
                  <table class="w-full text-left text-xs">
                    <thead class="bg-slate-50/70 border-b border-slate-100 text-[10px] font-bold uppercase text-slate-500">
                      <tr>
                        <th class="p-3">Port</th>
                        <th class="p-3">Direction</th>
                        <th class="p-3">Segment</th>
                        <th class="p-3">Official Explanation</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 text-[11px]">
                      <tr>
                        <td class="p-3 font-mono font-bold text-emerald-800">TCP 5500</td>
                        <td class="p-3"><span class="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold text-[9px]">IN / OUT</span></td>
                        <td class="p-3 text-slate-600">External</td>
                        <td class="p-3 text-slate-700">Message exchange between Security Servers</td>
                      </tr>
                      <tr>
                        <td class="p-3 font-mono font-bold text-emerald-800">TCP 5577</td>
                        <td class="p-3"><span class="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold text-[9px]">IN / OUT</span></td>
                        <td class="p-3 text-slate-600">External</td>
                        <td class="p-3 text-slate-700">Querying of OCSP responses between Security Servers</td>
                      </tr>
                      <tr>
                        <td class="p-3 font-mono font-bold text-blue-800">TCP 4001</td>
                        <td class="p-3"><span class="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-bold text-[9px]">OUTBOUND</span></td>
                        <td class="p-3 text-slate-600">External</td>
                        <td class="p-3 text-slate-700">Communication with the Zanzibar Central Server</td>
                      </tr>
                      <tr>
                        <td class="p-3 font-mono font-bold text-purple-800">TCP 80, 443</td>
                        <td class="p-3"><span class="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 font-bold text-[9px]">OUTBOUND</span></td>
                        <td class="p-3 text-slate-600">External</td>
                        <td class="p-3 text-slate-700">Downloading global configuration anchor, TSA & OCSP</td>
                      </tr>
                      <tr class="bg-rose-50/30">
                        <td class="p-3 font-mono font-bold text-rose-700">TCP 4000</td>
                        <td class="p-3"><span class="px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 font-bold text-[9px]">INBOUND</span></td>
                        <td class="p-3 text-rose-900 font-bold">Internal Only</td>
                        <td class="p-3 text-rose-800">UI & REST API (Must NOT be accessible from Internet!)</td>
                      </tr>
                      <tr>
                        <td class="p-3 font-mono font-bold text-slate-800">TCP 8080, 8443</td>
                        <td class="p-3"><span class="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-bold text-[9px]">INBOUND</span></td>
                        <td class="p-3 text-slate-600">Internal</td>
                        <td class="p-3 text-slate-700">Information system access points (IP filtering applied)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Table 2: Loopback Interface Ports (127.0.0.1) -->
              <div class="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs space-y-0">
                <div class="p-3.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <span class="text-xs font-bold text-slate-900 uppercase tracking-wider">Table 2: Loopback Interface Ports (127.0.0.1)</span>
                  <span class="text-[10px] font-mono text-slate-400">Process IPC</span>
                </div>
                <div class="overflow-x-auto">
                  <table class="w-full text-left text-xs">
                    <thead class="bg-slate-50/70 border-b border-slate-100 text-[10px] font-bold uppercase text-slate-500">
                      <tr>
                        <th class="p-3">Component</th>
                        <th class="p-3">Port</th>
                        <th class="p-3">Protocol</th>
                        <th class="p-3">Component Role</th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-slate-100 text-[11px]">
                      <tr>
                        <td class="p-3 font-semibold text-slate-900">PostgreSQL Database</td>
                        <td class="p-3 font-mono font-bold text-slate-800">5432</td>
                        <td class="p-3 text-slate-500">TCP</td>
                        <td class="p-3 text-slate-600">Default local PostgreSQL data store</td>
                      </tr>
                      <tr>
                        <td class="p-3 font-semibold text-slate-900">Signer Service</td>
                        <td class="p-3 font-mono font-bold text-slate-800">5559, 5560</td>
                        <td class="p-3 text-slate-500">TCP</td>
                        <td class="p-3 text-slate-600">Signer Admin & gRPC port</td>
                      </tr>
                      <tr>
                        <td class="p-3 font-semibold text-slate-900">X-Road Proxy Engine</td>
                        <td class="p-3 font-mono font-bold text-slate-800">5566, 5567</td>
                        <td class="p-3 text-slate-500">TCP</td>
                        <td class="p-3 text-slate-600">Proxy Admin & Proxy gRPC server port</td>
                      </tr>
                      <tr>
                        <td class="p-3 font-semibold text-slate-900">Configuration Client</td>
                        <td class="p-3 font-mono font-bold text-slate-800">5675</td>
                        <td class="p-3 text-slate-500">TCP</td>
                        <td class="p-3 text-slate-600">Configuration Client admin port</td>
                      </tr>
                      <tr>
                        <td class="p-3 font-semibold text-slate-900">Monitoring Daemons</td>
                        <td class="p-3 font-mono font-bold text-slate-800">2080, 2552</td>
                        <td class="p-3 text-slate-500">TCP</td>
                        <td class="p-3 text-slate-600">OP Monitoring & Environmental monitoring</td>
                      </tr>
                      <tr>
                        <td class="p-3 font-semibold text-slate-900">Audit Log Syslog</td>
                        <td class="p-3 font-mono font-bold text-slate-800">514</td>
                        <td class="p-3 text-slate-500">UDP</td>
                        <td class="p-3 text-slate-600">Secure immutable transaction audit log</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 3: Installation Process (Section 3 of eGAZ Guide) -->
          <div class="space-y-4">
            <div class="flex items-center gap-2">
              <span class="w-6 h-6 rounded-full bg-emerald-600 text-white font-black text-xs flex items-center justify-center">3</span>
              <h3 class="text-sm font-extrabold text-slate-900 tracking-tight">Installation Procedures (Ubuntu Server 24.04 LTS or latest)</h3>
            </div>

            <div class="space-y-3">
              <!-- Step 3.1: Preparing OS -->
              <div class="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-2.5">
                <div class="flex items-center justify-between">
                  <div>
                    <span class="text-xs font-extrabold text-slate-900">3.1 &bull; Preparing Operating System & Administrator Account</span>
                    <p class="text-[11px] text-slate-500 mt-0.5">Create administrative user (do NOT use username 'xroad') and configure UTF-8 system locale.</p>
                  </div>
                  <button
                    type="button"
                    (click)="copyCode(osPreparationCommands(), 'OS Preparation commands')"
                    class="px-2.5 py-1 rounded-lg text-[11px] font-bold text-slate-600 hover:text-emerald-700 bg-slate-100 hover:bg-emerald-50 border border-slate-200 transition cursor-pointer inline-flex items-center gap-1 shrink-0"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copy Commands</span>
                  </button>
                </div>
                <div class="p-3 rounded-xl bg-slate-950 text-emerald-400 font-mono text-[11px] overflow-x-auto shadow-inner">
                  <pre class="leading-relaxed">{{ osPreparationCommands() }}</pre>
                </div>
              </div>

              <!-- Step 3.2: Setup Package Repository -->
              <div class="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-2.5">
                <div class="flex items-center justify-between">
                  <div>
                    <span class="text-xs font-extrabold text-slate-900">3.2 &bull; Setup Zan X-Change Package Repository (eGAZ Archive)</span>
                    <p class="text-[11px] text-slate-500 mt-0.5">Download the official eGAZ signing key and add the jamiirepo package source.</p>
                  </div>
                  <button
                    type="button"
                    (click)="copyCode(repoSetupCommands(), 'Repository setup commands')"
                    class="px-2.5 py-1 rounded-lg text-[11px] font-bold text-slate-600 hover:text-emerald-700 bg-slate-100 hover:bg-emerald-50 border border-slate-200 transition cursor-pointer inline-flex items-center gap-1 shrink-0"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copy Commands</span>
                  </button>
                </div>
                <div class="p-3 rounded-xl bg-slate-950 text-emerald-400 font-mono text-[11px] overflow-x-auto shadow-inner">
                  <pre class="leading-relaxed">{{ repoSetupCommands() }}</pre>
                </div>
              </div>

              <!-- Step 3.5: Security Server Installation -->
              <div class="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-2.5">
                <div class="flex items-center justify-between">
                  <div>
                    <span class="text-xs font-extrabold text-slate-900">3.5 &bull; Install Security Server Package (xroad-securityserver)</span>
                    <p class="text-[11px] text-slate-500 mt-0.5">Installs core proxy, metaservices, messagelog archiver, and operational monitoring.</p>
                  </div>
                  <button
                    type="button"
                    (click)="copyCode(installCommands(), 'Installation command')"
                    class="px-2.5 py-1 rounded-lg text-[11px] font-bold text-slate-600 hover:text-emerald-700 bg-slate-100 hover:bg-emerald-50 border border-slate-200 transition cursor-pointer inline-flex items-center gap-1 shrink-0"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copy Command</span>
                  </button>
                </div>
                <div class="p-3 rounded-xl bg-slate-950 text-emerald-400 font-mono text-[11px] overflow-x-auto shadow-inner">
                  <pre class="leading-relaxed">{{ installCommands() }}</pre>
                </div>
                <div class="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-700 space-y-1">
                  <div class="font-bold text-slate-900">Interactive Package Installation Prompts:</div>
                  <ul class="list-disc list-inside text-[11px] space-y-1 text-slate-600">
                    <li><strong>Account name:</strong> Enter the administrator username created in step 3.1.</li>
                    <li><strong>Database URL:</strong> Press Enter for local default PostgreSQL database.</li>
                    <li><strong>Subject DN for UI & REST API:</strong> Enter in format <code class="font-mono text-emerald-800">/CN=server.domain.tld</code>.</li>
                    <li><strong>Alternative Names (SAN):</strong> Enter in format <code class="font-mono text-emerald-800">IP:1.2.3.4,DNS:servername.domain.tld</code>.</li>
                  </ul>
                </div>
              </div>

              <!-- Step 3.6: Post-Installation Checks -->
              <div class="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-2.5">
                <div class="flex items-center justify-between">
                  <div>
                    <span class="text-xs font-extrabold text-slate-900">3.6 &bull; Post-Installation Verification & Service Checks</span>
                    <p class="text-[11px] text-slate-500 mt-0.5">Ensure all X-Road daemon units are loaded and running active.</p>
                  </div>
                  <button
                    type="button"
                    (click)="copyCode(serviceCheckCommands(), 'Service check command')"
                    class="px-2.5 py-1 rounded-lg text-[11px] font-bold text-slate-600 hover:text-emerald-700 bg-slate-100 hover:bg-emerald-50 border border-slate-200 transition cursor-pointer inline-flex items-center gap-1 shrink-0"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copy Command</span>
                  </button>
                </div>
                <div class="p-3 rounded-xl bg-slate-950 text-emerald-400 font-mono text-[11px] overflow-x-auto shadow-inner">
                  <pre class="leading-relaxed">{{ serviceCheckCommands() }}</pre>
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] font-mono text-slate-600 pt-1">
                  <div class="p-2 rounded bg-slate-50 border border-slate-200">&bull; xroad-proxy.service</div>
                  <div class="p-2 rounded bg-slate-50 border border-slate-200">&bull; xroad-signer.service</div>
                  <div class="p-2 rounded bg-slate-50 border border-slate-200">&bull; xroad-proxy-ui-api</div>
                  <div class="p-2 rounded bg-slate-50 border border-slate-200">&bull; xroad-confclient</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 4: Initial Web Configuration & Cert Signing (Section 4 of eGAZ Guide) -->
          <div class="space-y-4">
            <div class="flex items-center gap-2">
              <span class="w-6 h-6 rounded-full bg-emerald-600 text-white font-black text-xs flex items-center justify-center">4</span>
              <h3 class="text-sm font-extrabold text-slate-900 tracking-tight">Security Server Initial Configuration (Section 4)</h3>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <!-- Step 4.1 & 4.2 -->
              <div class="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-2">
                <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                  Step 4.1 &bull; Anchor & Member
                </span>
                <div class="text-xs font-bold text-slate-900">Upload Anchor & Owner Member</div>
                <ul class="list-disc list-inside text-[11px] text-slate-600 space-y-1">
                  <li>Upload the downloaded <code class="text-emerald-700 font-bold">anchor XML</code> file.</li>
                  <li>Choose Member Class (<code class="text-slate-800">Gov</code>, <code class="text-slate-800">edu</code>, <code class="text-slate-800">fin</code>, <code class="text-slate-800">mno</code>).</li>
                  <li>Member Code: <strong class="text-slate-800">{{ memberCode() }}</strong>.</li>
                  <li>Security Server Code: <strong class="text-slate-800">{{ serverCode() }}</strong>.</li>
                </ul>
              </div>

              <!-- Step 4.2 & 4.3 -->
              <div class="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-2">
                <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-800 border border-blue-200">
                  Step 4.2 & 4.3 &bull; Token & Keys
                </span>
                <div class="text-xs font-bold text-slate-900">Token PIN & Key CSRs</div>
                <ul class="list-disc list-inside text-[11px] text-slate-600 space-y-1">
                  <li>Set Software Token PIN and log in.</li>
                  <li>Generate <strong>Signing Key</strong> & CSR (<code class="text-slate-800">JXR CA</code>, Format: PEM).</li>
                  <li>Generate <strong>Authentication Key</strong> & CSR (<code class="text-slate-800">JXR CA</code>, Format: PEM).</li>
                  <li>Download generated CSR files.</li>
                </ul>
              </div>

              <!-- Step 4.4 - 4.6 -->
              <div class="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-2">
                <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-50 text-teal-800 border border-teal-200">
                  Step 4.4 - 4.6 &bull; CA Server
                </span>
                <div class="text-xs font-bold text-slate-900">Sign Keys & Import Certs</div>
                <ul class="list-disc list-inside text-[11px] text-slate-600 space-y-1">
                  <li>Open CA Server: <a href="http://jxrcademo.nida.go.tz:8804" target="_blank" class="text-emerald-700 underline font-semibold">http://jxrcademo.nida.go.tz:8804</a></li>
                  <li>Upload & sign Signing key (Type: Sign).</li>
                  <li>Upload & sign Authentication key (Type: Auth).</li>
                  <li>Import certificates, send registration request, and activate Auth Key.</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Section 5: Troubleshooting & Error Handling (Section 4.10 - 4.12 of eGAZ Guide) -->
          <div class="p-5 rounded-2xl bg-slate-900 text-white shadow-xl border border-slate-800 space-y-4">
            <div class="flex items-center gap-2 border-b border-slate-800 pb-3">
              <svg class="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h4 class="text-xs font-extrabold uppercase tracking-wider text-amber-400">eGAZ Troubleshooting & Error Handling (Section 4.10 - 4.12)</h4>
                <p class="text-[11px] text-slate-400">Direct fixes for the most common Security Server installation error cases.</p>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <!-- Error 1: LC_ALL Locale -->
              <div class="p-3.5 rounded-xl bg-slate-950 border border-slate-800 space-y-2">
                <div class="font-mono text-rose-400 text-[11px]">Cannot set LC_ALL to default locale</div>
                <p class="text-[11px] text-slate-400">Occurs when system UTF-8 language pack is missing.</p>
                <div class="p-2 rounded bg-black/60 font-mono text-[10px] text-emerald-400 select-all">
                  sudo apt-get install -y language-pack-en<br />
                  sudo locale-gen en_US.UTF-8<br />
                  sudo update-locale en_US.UTF-8
                </div>
              </div>

              <!-- Error 2: PostgreSQL UTF8 -->
              <div class="p-3.5 rounded-xl bg-slate-950 border border-slate-800 space-y-2">
                <div class="font-mono text-rose-400 text-[11px]">postgreSQL is not UTF8 compatible</div>
                <p class="text-[11px] text-slate-400">Recreate PostgreSQL cluster with UTF8 encoding:</p>
                <div class="p-2 rounded bg-black/60 font-mono text-[10px] text-emerald-400 select-all">
                  sudo pg_dropcluster --stop 16 main<br />
                  LC_ALL="en_US.UTF-8" sudo pg_createcluster --start 16 main<br />
                  sudo apt-get -f install
                </div>
              </div>

              <!-- Error 3: Port 5432 -->
              <div class="p-3.5 rounded-xl bg-slate-950 border border-slate-800 space-y-2">
                <div class="font-mono text-rose-400 text-[11px]">Is postgres running on port 5432?</div>
                <p class="text-[11px] text-slate-400">Verify listening port in postgresql.conf:</p>
                <div class="p-2 rounded bg-black/60 font-mono text-[10px] text-emerald-400 select-all">
                  sudo nano /etc/postgresql/16/main/postgresql.conf<br />
                  sudo systemctl restart postgresql<br />
                  sudo apt-get -f install
                </div>
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Add / Edit Security Server API Key Form Modal -->
      <app-modal
        [isOpen]="showModal()"
        [title]="isEditing() ? 'Edit Security Server API Key' : 'Add Security Server API Key'"
        maxWidth="max-w-2xl"
        (close)="showModal.set(false)"
      >
        <form (ngSubmit)="onSaveApiKey()" class="space-y-4">
          <!-- Role access explanation callout -->
          <div class="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
            <div class="flex items-center gap-2">
              <span class="text-xs font-bold text-slate-800">Role Access:</span>
              <span
                class="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wide border inline-flex items-center gap-1"
                [ngClass]="getRoleBadgeClass(keyForm.role)"
              >
                {{ getRoleName(keyForm.role) }}
              </span>
            </div>
            <p class="text-xs text-slate-600 leading-relaxed">
              {{ getRoleDescription(keyForm.role) }}
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- API Key Role / Type -->
            <div class="md:col-span-2">
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">API Key Role / Type *</label>
              <select
                [(ngModel)]="keyForm.role"
                name="role"
                required
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs font-semibold focus:border-emerald-500 focus:outline-none"
              >
                @for (r of roles; track r.code) {
                  <option [value]="r.code">{{ r.name }}</option>
                }
              </select>
            </div>

            <!-- Key Label / Application Identifier -->
            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Key Label / Identifier *</label>
              <input
                type="text"
                [(ngModel)]="keyForm.label"
                name="label"
                required
                placeholder="e.g. Primary Security Officer Key"
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500 focus:outline-none"
              />
            </div>

            <!-- Security Server Node Code -->
            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Security Server Node Code *</label>
              <input
                type="text"
                [(ngModel)]="keyForm.serverCode"
                name="serverCode"
                required
                placeholder="e.g. SS-ZANX-NODE-01"
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs font-mono focus:border-emerald-500 focus:outline-none"
              />
            </div>

            <!-- Host Endpoint URL -->
            <div class="md:col-span-2">
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Security Server Host Endpoint *</label>
              <input
                type="text"
                [(ngModel)]="keyForm.hostEndpoint"
                name="hostEndpoint"
                required
                placeholder="https://ss01.zan-x.egov.go.tz:4000"
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs font-mono focus:border-emerald-500 focus:outline-none"
              />
            </div>

            <!-- The Security Server API Key Token (Inserted by user/admin) -->
            <div class="md:col-span-2">
              <div class="flex items-center justify-between mb-1">
                <label class="block text-xs font-bold text-slate-700 uppercase">
                  Insert Security Server API Key Token *
                </label>
                <button
                  type="button"
                  (click)="keyForm.showTokenInForm = !keyForm.showTokenInForm"
                  class="text-[11px] text-emerald-700 hover:text-emerald-900 font-semibold cursor-pointer"
                >
                  {{ keyForm.showTokenInForm ? 'Mask Token' : 'Reveal Token' }}
                </button>
              </div>
              <textarea
                [(ngModel)]="keyForm.tokenValue"
                name="tokenValue"
                required
                rows="3"
                placeholder="Paste or insert the Security Server API key token for this role here..."
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-slate-50 text-slate-900 text-xs font-mono focus:bg-white focus:border-emerald-500 focus:outline-none resize-none"
              ></textarea>
              <p class="text-[11px] text-slate-400 mt-1">
                Enter the exact API key token generated for this role in your Security Server console or CLI.
              </p>
            </div>
          </div>

          <div class="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              (click)="showModal.set(false)"
              class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-sm cursor-pointer"
            >
              {{ isEditing() ? 'Save Changes' : 'Insert API Key' }}
            </button>
          </div>
        </form>
      </app-modal>
    </div>
  `
})
export class EngineConfigComponent implements OnInit {
  authService = inject(AuthService);
  toastService = inject(ToastService);
  route = inject(ActivatedRoute);
  router = inject(Router);

  roles = SECURITY_ROLES;

  activeTab = signal<'overview' | 'installation'>('installation');

  isTesting = signal<boolean>(false);
  isSyncing = signal<boolean>(false);
  latency = signal<string>('18ms');
  lastSyncTime = signal<string>('2026-09-28 09:15 UTC');

  serverCode = signal<string>('SS-ZANX-NODE-01');
  serverHost = signal<string>('https://ss01.zan-x.egov.go.tz:4000');
  memberCode = signal<string>('ZAN-GOV-042');

  showModal = signal<boolean>(false);
  isEditing = signal<boolean>(false);
  editingRecordId = signal<string | null>(null);

  searchQuery = '';
  selectedRoleFilter = 'ALL';
  selectedStatusFilter = 'ALL';

  keyRecords = signal<SecurityServerApiKeyRecord[]>([
    {
      id: 'REC-001',
      serverName: 'Security Server Master Node',
      serverCode: 'SS-ZANX-NODE-01',
      hostEndpoint: 'https://ss01.zan-x.egov.go.tz:4000',
      role: 'SECURITY_OFFICER',
      roleName: 'Security Officer',
      roleDescription: 'Manages security-related settings such as authentication keys, certificates, and security tokens.',
      label: 'Core Security Officer Management Key',
      tokenValue: 'zx_sec_off_8829a41b9e20cd711f5a09',
      status: 'ACTIVE',
      createdAt: '2026-06-15',
      lastUsed: '12 mins ago',
      showSecret: false
    },
    {
      id: 'REC-002',
      serverName: 'Security Server Master Node',
      serverCode: 'SS-ZANX-NODE-01',
      hostEndpoint: 'https://ss01.zan-x.egov.go.tz:4000',
      role: 'REGISTRATION_OFFICER',
      roleName: 'Registration Officer',
      roleDescription: "Manages the Security Server's registration with the Central Server, including members, clients, and service registration.",
      label: 'Central Server Registration Sync Key',
      tokenValue: 'zx_reg_off_5193c72e01b4da8992ef14',
      status: 'ACTIVE',
      createdAt: '2026-07-01',
      lastUsed: '1 hour ago',
      showSecret: false
    },
    {
      id: 'REC-003',
      serverName: 'Security Server Master Node',
      serverCode: 'SS-ZANX-NODE-01',
      hostEndpoint: 'https://ss01.zan-x.egov.go.tz:4000',
      role: 'SERVER_ADMINISTRATOR',
      roleName: 'Server Administrator',
      roleDescription: 'Manages Security Server configuration, such as services, clients, connections, and operational settings.',
      label: 'Security Server Operations & Services Admin Key',
      tokenValue: 'zx_srv_adm_3490b61f88cd12ae05b672',
      status: 'ACTIVE',
      createdAt: '2026-08-10',
      lastUsed: '2 mins ago',
      showSecret: false
    },
    {
      id: 'REC-004',
      serverName: 'Security Server Master Node',
      serverCode: 'SS-ZANX-NODE-01',
      hostEndpoint: 'https://ss01.zan-x.egov.go.tz:4000',
      role: 'SYSTEM_ADMINISTRATOR',
      roleName: 'System Administrator',
      roleDescription: 'Manages the underlying server/system, including system-level configuration and maintenance.',
      label: 'Underlying Host Infrastructure Root Admin Key',
      tokenValue: 'zx_sys_adm_9912a77e50bb34cd811a43',
      status: 'ACTIVE',
      createdAt: '2026-05-20',
      lastUsed: 'Just now',
      showSecret: false
    },
    {
      id: 'REC-005',
      serverName: 'Central Registry Coordinator',
      serverCode: 'SS-ZANX-NODE-01',
      hostEndpoint: 'https://central.zan-x.egov.go.tz:4000',
      role: 'SERVER_OBSERVER',
      roleName: 'Server Observer',
      roleDescription: 'Read-only access. Can view Security Server configuration, status, and information.',
      label: 'Central Audit & Health Monitoring Observer Key',
      tokenValue: 'zx_srv_obs_1204d88e45ac23fa672c91',
      status: 'ACTIVE',
      createdAt: '2026-04-12',
      lastUsed: '3 hours ago',
      showSecret: false
    }
  ]);

  keyForm = {
    serverName: 'Security Server Master Node',
    serverCode: 'SS-ZANX-NODE-01',
    hostEndpoint: 'https://ss01.zan-x.egov.go.tz:4000',
    role: 'SECURITY_OFFICER' as SecurityServerRole,
    label: '',
    tokenValue: '',
    showTokenInForm: true
  };

  activeTokensCount = computed(() => {
    return this.keyRecords().filter(r => r.status === 'ACTIVE').length;
  });

  filteredRecords = computed(() => {
    let list = this.keyRecords();

    if (this.selectedRoleFilter !== 'ALL') {
      list = list.filter(r => r.role === this.selectedRoleFilter);
    }

    if (this.selectedStatusFilter !== 'ALL') {
      list = list.filter(r => r.status === this.selectedStatusFilter);
    }

    const q = this.searchQuery.toLowerCase().trim();
    if (q) {
      list = list.filter(r =>
        r.label.toLowerCase().includes(q) ||
        r.serverName.toLowerCase().includes(q) ||
        r.serverCode.toLowerCase().includes(q) ||
        r.roleName.toLowerCase().includes(q) ||
        r.roleDescription.toLowerCase().includes(q) ||
        r.tokenValue.toLowerCase().includes(q)
      );
    }
    return list;
  });

  ngOnInit(): void {
    const instCode = this.authService.institutionUid();
    if (instCode) {
      this.memberCode.set(instCode);
    }

    // Check if URL matches security server / keys tab
    if (
      this.router.url.includes('/security-server') ||
      this.router.url.includes('/keys') ||
      this.router.url.includes('/configurations')
    ) {
      this.activeTab.set('overview');
    } else {
      this.activeTab.set('installation');
    }
  }

  setTab(tab: 'overview' | 'installation') {
    this.activeTab.set(tab);
    if (tab === 'overview') {
      this.router.navigate(['/engine/security-server'], { replaceUrl: true });
    } else {
      this.router.navigate(['/engine'], { replaceUrl: true });
    }
  }

  osPreparationCommands(): string {
    return `# 1. Add Zan X-Change system administrator user (do not use 'xroad')
sudo adduser sec-admin

# 2. Set the operating system locale in /etc/environment
sudo sh -c 'echo "LC_ALL=en_US.UTF-8" >> /etc/environment'

# 3. Ensure that locales and software-properties-common are installed
sudo apt-get update && sudo apt-get install -y locales software-properties-common

# 4. Generate and update locale configuration
sudo locale-gen en_US.UTF-8
sudo update-locale LC_ALL=en_US.UTF-8`;
  }

  repoSetupCommands(): string {
    return `# 1. Add Zan X-Change repository signing key (official eGAZ archive)
sudo apt update && sudo apt install -y curl gnupg
sudo curl http://zanxccademo.egoz.go.tz:7376/$(lsb_release -sc)/public.gpg | sudo gpg --yes --dearmor -o /usr/share/keyrings/jamiirepo-$(lsb_release -sc).gpg

# 2. Add Zan X-Change package repository metadata
echo "deb [arch=any signed-by=/usr/share/keyrings/jamiirepo-$(lsb_release -sc).gpg] http://zanxccademo.egoz.go.tz:7376/$(lsb_release -sc)/ stable main" | sudo tee /etc/apt/sources.list.d/jamiirepo-$(lsb_release -sc).list

# 3. Update package lists
sudo apt update`;
  }

  installCommands(): string {
    return `# Install Zan X-Change Security Server package on Ubuntu Server 24.04 LTS or latest
sudo apt install -y xroad-securityserver`;
  }

  serviceCheckCommands(): string {
    return `# Verify all X-Road units are loaded, active and running
sudo systemctl list-units "xroad-*"`;
  }

  copyCode(code: string, label: string): void {
    navigator.clipboard.writeText(code).then(() => {
      this.toastService.success(`${label} copied to clipboard!`);
    }).catch(() => {
      this.toastService.info('Copied commands to clipboard');
    });
  }

  getRoleDef(code: SecurityServerRole): RoleDefinition {
    return this.roles.find(r => r.code === code) || this.roles[0];
  }

  getRoleName(code: SecurityServerRole): string {
    return this.getRoleDef(code).name;
  }

  getRoleDescription(code: SecurityServerRole): string {
    return this.getRoleDef(code).description;
  }

  getRoleBadgeClass(code: SecurityServerRole): string {
    return this.getRoleDef(code).badgeClass;
  }

  getMaskedToken(val: string): string {
    if (!val || val.length < 12) return '••••••••••••••••';
    return val.substring(0, 10) + '••••••••' + val.slice(-4);
  }

  copyToken(token: string): void {
    navigator.clipboard.writeText(token).then(() => {
      this.toastService.success('API key token copied to clipboard!');
    }).catch(() => {
      this.toastService.info('Copied: ' + token);
    });
  }

  runPingTest(): void {
    this.isTesting.set(true);
    setTimeout(() => {
      this.isTesting.set(false);
      this.latency.set(Math.floor(14 + Math.random() * 8) + 'ms');
      this.toastService.success('Security Server ping successful: Node is responsive and healthy.');
    }, 800);
  }

  syncCentralServices(): void {
    this.isSyncing.set(true);
    setTimeout(() => {
      this.isSyncing.set(false);
      const now = new Date();
      this.lastSyncTime.set(now.toISOString().replace('T', ' ').substring(0, 16) + ' UTC');
      this.toastService.success('Node synchronized with Zanzibar Central Trust Server.');
    }, 1200);
  }

  downloadAnchor(): void {
    const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<configurationAnchor xmlns="http://x-road.eu/xsd/xroad-configuration-anchor.xsd">
  <instanceIdentifier>ZANX-CORE</instanceIdentifier>
  <source>
    <downloadURL>https://central.zan-x.egov.go.tz/internalconf</downloadURL>
    <verificationCert>MIIDXDCCAkSgAwIBAgIU...</verificationCert>
  </source>
</configurationAnchor>`;

    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `configuration-anchor-ZANX-CORE-${this.serverCode()}.xml`;
    a.click();
    window.URL.revokeObjectURL(url);
    this.toastService.success('Global Configuration Anchor XML downloaded successfully.');
  }

  openAddModal(): void {
    this.isEditing.set(false);
    this.editingRecordId.set(null);
    this.keyForm = {
      serverName: 'Security Server Master Node',
      serverCode: this.serverCode(),
      hostEndpoint: this.serverHost(),
      role: 'SECURITY_OFFICER',
      label: '',
      tokenValue: '',
      showTokenInForm: true
    };
    this.showModal.set(true);
  }

  openEditModal(rec: SecurityServerApiKeyRecord): void {
    this.isEditing.set(true);
    this.editingRecordId.set(rec.id);
    this.keyForm = {
      serverName: rec.serverName,
      serverCode: rec.serverCode,
      hostEndpoint: rec.hostEndpoint,
      role: rec.role,
      label: rec.label,
      tokenValue: rec.tokenValue,
      showTokenInForm: true
    };
    this.showModal.set(true);
  }

  onSaveApiKey(): void {
    if (!this.keyForm.label.trim()) {
      this.toastService.warning('Please provide a descriptive key label.');
      return;
    }
    if (!this.keyForm.tokenValue.trim()) {
      this.toastService.warning('Please insert the Security Server API key token.');
      return;
    }

    const roleDef = this.getRoleDef(this.keyForm.role);

    if (this.isEditing() && this.editingRecordId()) {
      this.keyRecords.update(list =>
        list.map(r => {
          if (r.id === this.editingRecordId()) {
            return {
              ...r,
              serverCode: this.keyForm.serverCode,
              hostEndpoint: this.keyForm.hostEndpoint,
              role: this.keyForm.role,
              roleName: roleDef.name,
              roleDescription: roleDef.description,
              label: this.keyForm.label.trim(),
              tokenValue: this.keyForm.tokenValue.trim(),
              lastUsed: 'Just updated'
            };
          }
          return r;
        })
      );
      this.toastService.success(`Security Server key for "${roleDef.name}" updated successfully.`);
    } else {
      const newRecord: SecurityServerApiKeyRecord = {
        id: 'REC-' + (this.keyRecords().length + 1).toString().padStart(3, '0'),
        serverName: this.keyForm.serverName || 'Security Server Master Node',
        serverCode: this.keyForm.serverCode || this.serverCode(),
        hostEndpoint: this.keyForm.hostEndpoint || this.serverHost(),
        role: this.keyForm.role,
        roleName: roleDef.name,
        roleDescription: roleDef.description,
        label: this.keyForm.label.trim(),
        tokenValue: this.keyForm.tokenValue.trim(),
        status: 'ACTIVE',
        createdAt: new Date().toISOString().substring(0, 10),
        lastUsed: 'Never',
        showSecret: false
      };
      this.keyRecords.update(list => [newRecord, ...list]);
      this.toastService.success(`Security Server key for "${roleDef.name}" inserted successfully.`);
    }

    this.showModal.set(false);
  }

  revokeToken(rec: SecurityServerApiKeyRecord): void {
    rec.status = 'REVOKED';
    this.toastService.warning(`Security Server API key "${rec.label}" has been revoked.`);
  }
}
